import { useState, ChangeEvent, useRef, useEffect, useCallback } from 'react';
import { Material, ProductTemplate, TemplateComponent } from '../types';
import { generateContentWithCache } from '../lib/geminiService';
import { parseAIJSON } from '../utils/aiUtils';
import { compressImage } from '../utils/imageUtils';
import { parseFlexibleTimeToMinutes } from '../utils/formatUtils';

export interface PendingRecipeItem extends TemplateComponent {
  _tempName?: string;
  _tempUnit?: string;
  _tempPrice?: number;
  _isNew?: boolean;
}

export interface PendingRecipe extends Partial<ProductTemplate> {
  items?: PendingRecipeItem[];
}

export interface UseRecipeAIProps {
  catalog: Material[];
  onSaveRecipe: (recipe: ProductTemplate) => Promise<void> | void;
  onSaveMaterials: (materials: Material[]) => Promise<void> | void;
  userId: string;
}

const normalizeString = (str: string): string => {
  return str.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
};

export const findBestMaterialMatch = (ingredientName: string, materialsList: Material[]): string => {
  if (!ingredientName) return '';
  const searchStr = normalizeString(ingredientName);
  
  // Exact match
  const exact = materialsList.find(m => normalizeString(m.name) === searchStr);
  if (exact) return exact.id || '';

  // Word boundary scoring
  const searchWords = searchStr.split(/\s+/).filter(w => w.length > 2);
  let bestMatch: string | null = null;
  let maxScore = 0;

  materialsList.forEach(m => {
    const matName = normalizeString(m.name);
    let score = 0;
    if (matName.includes(searchStr) || searchStr.includes(matName)) {
       score += 10;
    }
    searchWords.forEach(word => {
      if (matName.includes(word)) score += 1;
    });
    if (score > maxScore) {
      maxScore = score;
      bestMatch = m.id || null;
    }
  });

  return maxScore > 0 ? (bestMatch || '') : '';
};

export function useRecipeAI({ catalog, onSaveRecipe, onSaveMaterials, userId }: UseRecipeAIProps) {
  const [inputValue, setInputValue] = useState<string>('');
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [processingStep, setProcessingStep] = useState<string>('');
  const [pendingRecipes, setPendingRecipes] = useState<PendingRecipe[]>([]);

  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  const isProcessingRef = useRef(false);

  const processWithAI = useCallback(async (text?: string, imageBase64?: string): Promise<void> => {
    if (!text && !imageBase64) return;

    if (isProcessingRef.current) return;
    isProcessingRef.current = true;
    setIsProcessing(true);
    setProcessingStep('Iniciando análise...');

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    
    try {
      const prompt = `Você é um chef e extrator do sistema R-MAX.
Extraia as receitas listadas no texto ou imagem fornecida.
Retorne APENAS UM JSON no esquema exato abaixo:
{
  "recipes": [
    {
      "name": "Nome da Receita",
      "time": 30,
      "items": [
        { 
          "name": "Nome do Ingrediente Puro (ex: farinha, ovo, açúcar)", 
          "qty": 2.5,
          "unit": "kg",
          "price": 15.00
        }
      ]
    }
  ]
}
Regras:
1. "time": Tempo em minutos. Se ausente, deduza ou ponha 30.
2. "qty": NUMERAL decimal/inteiro apenas. Se o usuário diz "500g de manteiga", qty é 500. Se diz "1 litro", qty é 1000.
3. "unit": A unidade (gr, ml, un, kg, lt). Essencial para custo: sempre tente extrair o peso/volume real (ex: 500 em vez de 1 se for 500g).
4. "price": O preço PAGO por aquela quantidade total mencionada (ex: R$ 27,90 pelos 500g).`;

      setProcessingStep('Consultando o R-Max...');
      
      const parts: Array<{ text: string } | { inlineData: { mimeType: string; data: string } }> = [
        { text: `${prompt}${text ? `\n\nMensagem do Usuário: "${text}"` : ''}` }
      ];
      
      if (imageBase64) {
        setProcessingStep('Processando imagem...');
        parts.push({
          inlineData: {
            mimeType: "image/jpeg",
            data: imageBase64.split(',')[1]
          }
        });
      }
      
      const contents = [{ role: 'user', parts }];

      const response = await generateContentWithCache({
        model: 'gemini-3.5-flash-lite',
        contents,
        config: {
          generationConfig: {
            responseMimeType: 'application/json'
          }
        },
        signal: abortControllerRef.current.signal
      });
      
      setProcessingStep('Organizando ingredientes...');
      
      interface AIRecipeItem {
        name?: string;
        qty?: number;
        unit?: string;
        price?: number;
      }
      
      interface AIRecipe {
        name?: string;
        time?: number | string;
        items?: AIRecipeItem[];
      }

      const parsed = parseAIJSON<any>(response.text, null);
      let rawRecipes: AIRecipe[] = [];
      if (Array.isArray(parsed)) {
        rawRecipes = parsed;
      } else if (parsed && typeof parsed === 'object') {
        if (Array.isArray(parsed.recipes)) {
          rawRecipes = parsed.recipes;
        } else if (parsed.name || parsed.items) {
          rawRecipes = [parsed];
        }
      }

      let results: PendingRecipe[] = [];

      if (rawRecipes.length > 0) {
        results = rawRecipes.map(recipe => {
          const recipeId = crypto.randomUUID();
          const items: PendingRecipeItem[] = Array.isArray(recipe.items) ? recipe.items.map((item) => {
            const materialId = findBestMaterialMatch(item.name || '', catalog);
            return {
              id: crypto.randomUUID(),
              templateId: recipeId,
              materialId: materialId || '',
              _tempName: item.name,
              _tempUnit: item.unit || 'un',
              _tempPrice: typeof item.price === 'number' ? item.price : 0,
              _isNew: !materialId,
              quantity: typeof item.qty === 'number' ? item.qty : 1
            };
          }) : [];

          const parsedTime = parseFlexibleTimeToMinutes(recipe.time ?? 30);
          return {
            id: recipeId,
            userId,
            name: recipe.name || 'Receita Extraída',
            executionTimeMinutes: parsedTime > 0 ? parsedTime : 30,
            items,
            createdAt: new Date().toISOString()
          };
        });
      }

      if (results.length > 0) {
        setPendingRecipes(prev => [...prev, ...results]);
        setInputValue('');
      } else {
        alert("O R-Max não conseguiu interpretar a imagem ou texto. Tente novamente.");
      }
    } catch (err: unknown) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        return;
      }
      console.error('AI processing error:', err);
      const errorMessage = err instanceof Error ? err.message : String(err);
      alert(errorMessage || "Falha no processamento visual. Verifique sua conexão.");
    } finally {
      isProcessingRef.current = false;
      if (!abortControllerRef.current?.signal.aborted) {
        setIsProcessing(false);
        setProcessingStep('');
      }
    }
  }, [catalog, userId]);

  const handleImageExtraction = useCallback(async (e: ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const compressedDataUrl = await compressImage(file, { purpose: 'ocr' });
      await processWithAI(undefined, compressedDataUrl);
    } catch (err: unknown) {
      console.error('Erro ao comprimir imagem da receita:', err);
      const errorMessage = err instanceof Error ? err.message : String(err);
      alert(errorMessage || 'Erro ao processar imagem da receita.');
    }
  }, [processWithAI]);

  const confirmPendingRecipe = useCallback(async (recipe: PendingRecipe): Promise<void> => {
    if (!recipe.name) {
       alert("Dê um nome à receita!");
       return;
    }
    
    const newMaterials: Material[] = [];
    const updatedItems = (recipe.items || []).map(item => {
      if (item._isNew || !item.materialId) {
        const newId = crypto.randomUUID();
        const material: Material = {
          id: newId,
          userId,
          name: item._tempName || 'Novo Material',
          unit: item._tempUnit || 'un',
          pricePaid: item._tempPrice || 0,
          yieldAmount: item.quantity || 1,
          category: 'variável'
        };
        newMaterials.push(material);
        return { ...item, materialId: newId };
      }
      return item;
    });

    try {
      if (newMaterials.length > 0) {
        await onSaveMaterials([...catalog, ...newMaterials]);
      }

      const cleanItems = updatedItems.map(i => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { _tempName, _tempUnit, _tempPrice, _isNew, ...rest } = i;
        return rest as TemplateComponent;
      });

      await onSaveRecipe({
        ...recipe,
        items: cleanItems,
        id: recipe.id || crypto.randomUUID(),
        userId,
        createdAt: recipe.createdAt || new Date().toISOString()
      } as ProductTemplate);
      
      setPendingRecipes(prev => prev.filter(p => p.id !== recipe.id));
    } catch (err: unknown) {
      console.error('Error saving pending recipe:', err);
      const errorMessage = err instanceof Error ? err.message : String(err);
      alert(errorMessage || 'Erro ao salvar receita e insumos.');
    }
  }, [catalog, onSaveMaterials, onSaveRecipe, userId]);

  const updatePendingRecipeField = useCallback(<K extends keyof PendingRecipe>(
    recipeId: string,
    field: K,
    value: PendingRecipe[K]
  ): void => {
    setPendingRecipes(prev => prev.map(r => 
      r.id === recipeId ? { ...r, [field]: value } : r
    ));
  }, []);
  
  const updatePendingItem = useCallback(<K extends keyof PendingRecipeItem>(
    recipeId: string,
    itemId: string,
    field: K,
    value: PendingRecipeItem[K]
  ): void => {
    setPendingRecipes(prev => prev.map(r => {
      if (r.id !== recipeId) return r;
      return {
         ...r,
         items: r.items?.map(i => i.id === itemId ? { ...i, [field]: value } : i)
      };
    }));
  }, []);

  return {
    inputValue,
    setInputValue,
    isProcessing,
    processingStep,
    pendingRecipes,
    setPendingRecipes,
    processWithAI,
    handleImageExtraction,
    confirmPendingRecipe,
    updatePendingRecipeField,
    updatePendingItem,
    findBestMaterialMatch
  };
}
